import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Form,
    Input,
    Button,
    Card,
    Row,
    Col,
    Select,
    InputNumber,
    Upload,
    Modal,
    DatePicker,
    message,
    Breadcrumb
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import dayjs from 'dayjs';
import henceforthApi from '../utils/henceforthApi';
import GoogleMapComponent from '../components/GoogleMap';

const { TextArea } = Input;
// const { Option } = Select;

const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

const genderOptions = [
    { value: "MALE", label: "Male" },
    { value: "FEMALE", label: "Female" },
    { value: "NO_PREFERENCE", label: "No Preference" },
];

const timerOptions = [
    { value: 6, label: "6" },
    { value: 12, label: "12" },
    { value: 24, label: "24" },
];

const raceOptions = [
    { value: "MARATHON", label: "Marathon race" },
    { value: "QUICK_ADMIN", label: "Quick race" },
];

const EditRace = () => {
    const { id } = useParams(); // Get race ID from URL params
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();

    // Get race_type from URL query params
    const searchParams = new URLSearchParams(location.search);
    const raceType = searchParams.get('race_type');

    const [loading, setLoading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [previewTitle, setPreviewTitle] = useState("");
    const [fileList1, setFileList1] = useState([]);
    const [fileList2, setFileList2] = useState(null);
    const [dataId, setDataId] = useState("");

    // Google Maps related states
    const defaultCenter = {
        lat: 30.704649,
        lng: 76.717873,
    };
    const [startAddress, setStartAddress] = useState(null);
    const [endAddress, setEndAddress] = useState(null);
    const [startPlace, setStartPlace] = useState(null);
    const [endPlace, setEndPlace] = useState(null);
    const [distance, setDistance] = useState(null);
    const [snapshotUrl, setSnapshotUrl] = useState(null);

    // Watch for race_type changes in form
    const formRaceType = Form.useWatch("race_type", form);

    const beforeUpload = (file) => {
        const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
        if (!isJpgOrPng) {
            message.error("You can only upload JPG/PNG file!");
        }
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            message.error("Image must smaller than 2MB!");
        }
        return isJpgOrPng && isLt2M;
    };

    const handleCancel = () => setPreviewOpen(false);

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
        setPreviewTitle(
            file.name || file.url.substring(file.url.lastIndexOf("/") + 1)
        );
    };

    const handleChange1 = ({ fileList: newFileList }) => {
        setFileList1(newFileList);
        form.setFieldsValue({
            destination_banner: newFileList[0],
        });
    };

    const uploadButton = (
        <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    );

    // Fetch race data by ID
    const getData = async (raceId) => {
        try {
            setLoading(true);
            // Replace with your actual API call
            const { data } = await henceforthApi.Race.getById(raceId);

            setDataId(data._id);

            // Set form fields
            form.setFieldsValue({
                title: data.title,
                description: data.description,
                max_participants: data.max_participants,
                total_distance: data.total_distance / 1000,
                race_type: data.race_type,
                race_stop_timer: data.race_stop_timer,
                gender_preference: data.gender_preference,
                scheduled_time: data.scheduled_time ? dayjs(data.scheduled_time) : null,
                remaining_completion_time: data.remaining_completion_time
                    ? dayjs(data.remaining_completion_time)
                    : null,
                start_location_name: data?.start_location_name,
                end_location_name: data?.end_location_name
            });

            setEndPlace(data?.end_location_name)
            setStartPlace(data?.start_location_name)
            // Set distance for map
            setDistance(data.total_distance);

            // Set Google map values
            setStartAddress({
                location: {
                    lat: data.start_location.coordinates[1],
                    lng: data.start_location.coordinates[0],
                },
                place: data.start_location_name,
            });

            setEndAddress({
                location: {
                    lat: data.end_location.coordinates[1],
                    lng: data.end_location.coordinates[0],
                },
                place: data.end_location_name,
            });

            setSnapshotUrl(data.map_snapshot);
            setFileList2(data.destination_banner);

            // Set file list for upload component
            setFileList1([
                {
                    uid: "-2",
                    name: data.destination_banner,
                    status: "done",
                    url: `${henceforthApi.API_FILE_ROOT_ORIGINAL}${data.destination_banner}` // Replace with your file URL
                },
            ]);

        } catch (error) {
            console.error("Error fetching race data:", error);
            message.error("Failed to fetch race data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            getData(id);
        }
    }, [id]);

    useEffect(() => {
        if (form && distance) {
            form.setFieldsValue({ total_distance: distance / 1000 });
        }
    }, [form, distance]);

    const onFinish = async (values) => {
        const addressComponents1 = startPlace?.address_components || [];
        const addressComponents2 = endPlace?.address_components || [];

        const getComponent1 = (type) => {
            const component = addressComponents1.find((comp) =>
                comp.types.includes(type)
            );
            return component ? component.long_name : "";
        };

        const getComponent2 = (type) => {
            const component = addressComponents2.find((comp) =>
                comp.types.includes(type)
            );
            return component ? component.long_name : "";
        };

        let items = {
            race_id: dataId,
            title: String(values.title),
            max_participants: Number(values.max_participants),
            total_distance: Number(distance / 1000),
            race_type: String(values.race_type),
            race_stop_timer: Number(values.race_stop_timer),
            gender_preference: String(values.gender_preference),
            map_snapshot: String(snapshotUrl),
            start_location: {
                type: "Point",
                coordinates: [startAddress?.location?.lng, startAddress?.location?.lat],
            },
            start_location_name: startPlace + "," + getComponent1("locality"),
            end_location: {
                type: "Point",
                coordinates: [endAddress?.location?.lng, endAddress?.location?.lat],
            },
            end_location_name: endPlace + "," + getComponent2("locality"),
        };

        if (values.description) {
            items["description"] = String(values.description);
        }

        if (values.race_type === "MARATHON") {
            items["scheduled_time"] = new Date(values.scheduled_time).toISOString();
            items["remaining_completion_time"] = new Date(
                values.remaining_completion_time
            ).toISOString();
        }

        try {
            setLoading(true);

            // Handle file upload
            if (fileList2 === fileList1?.[0]?.name) {
                items["destination_banner"] = fileList2;
            } else {
                // Replace with your actual file upload API
                const apiImageRes1 = await henceforthApi.Common.uploadFile(
                    "file",
                    values.destination_banner.originFileObj
                );
                items["destination_banner"] = apiImageRes1?.data.file_name;

                // Mock file upload
                items["destination_banner"] = "uploaded_file.jpg";
            }

            console.log("Form values:", items);

            // Replace with your actual API call
            const apiRes = await henceforthApi.Race.update(items);

            // Mock success response
            //const apiRes = { message: "Race updated successfully!" };

            form.resetFields();
            message.success(apiRes?.message);

            // Navigate back to races list
            navigate("/race");

        } catch (error) {
            message.error("Failed to update race");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => {
        navigate(-1); // Go back to previous page
    };

    return (
        <div style={{ padding: '24px' }}>
            <Row gutter={[20, 20]}>
                <Col span={24}>
                    <Card className="common-card">
                        <div style={{ marginBottom: '16px' }}>
                            <Breadcrumb separator=">">
                                <Breadcrumb.Item>
                                    <span
                                        style={{ cursor: 'pointer', textDecoration: 'none' }}
                                        onClick={() => navigate('/')}
                                    >
                                        Home
                                    </span>
                                </Breadcrumb.Item>
                                <Breadcrumb.Item>Race</Breadcrumb.Item>
                                <Breadcrumb.Item>Edit Race</Breadcrumb.Item>
                            </Breadcrumb>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontWeight: 'bold' }}>
                                Edit Race
                            </h3>
                        </div>

                        <div className="card-form-wrapper">
                            <Form
                                form={form}
                                name="edit_race"
                                className="edit-race-form"
                                onFinish={onFinish}
                                scrollToFirstError
                                layout="vertical"
                            >
                                {/* Destination Banner Upload */}
                                <Form.Item
                                    name="destination_banner"
                                    label="Destination Banner"
                                    rules={[
                                        {
                                            required: fileList1.length > 0 ? false : true,
                                            message: "Please upload Destination Banner",
                                        },
                                    ]}
                                >
                                    <ImgCrop aspect={1}>
                                        <Upload
                                            customRequest={({ onSuccess }) => onSuccess("ok")}
                                            name="destination_banner"
                                            listType="picture-card"
                                            fileList={fileList1}
                                            onPreview={handlePreview}
                                            beforeUpload={beforeUpload}
                                            onChange={handleChange1}
                                        >
                                            {fileList1.length > 0 ? null : uploadButton}
                                        </Upload>
                                    </ImgCrop>
                                </Form.Item>

                                <Modal
                                    open={previewOpen}
                                    title={previewTitle}
                                    footer={null}
                                    onCancel={handleCancel}
                                >
                                    <img
                                        alt="preview"
                                        style={{ width: "100%" }}
                                        src={previewImage}
                                    />
                                </Modal>

                                {/* Title */}
                                <Form.Item
                                    name="title"
                                    rules={[
                                        {
                                            required: true,
                                            whitespace: true,
                                            message: "Please enter race title",
                                        },
                                    ]}
                                    label="Title"
                                >
                                    <Input placeholder="Enter Race title" />
                                </Form.Item>

                                {/* Description */}
                                <Form.Item
                                    name="description"
                                    rules={[
                                        {
                                            required: raceType === "MARATHON" ? true : false,
                                            whitespace: true,
                                            message: "Please enter race description",
                                        },
                                    ]}
                                    label="Description"
                                >
                                    <TextArea rows={4} placeholder="Enter Race description" />
                                </Form.Item>

                                {/* Google Map Component - You'll need to implement this */}
                                <div className="h-[600px] w-100" style={{ height: "600px" }}>
                                    <GoogleMapComponent
                                        defaultCenter={defaultCenter}
                                        setStartAddress={setStartAddress}
                                        setEndAddress={setEndAddress}
                                        setStartPlace={setStartPlace}
                                        setEndPlace={setEndPlace}
                                        setDistanced={setDistance}
                                        setSnapshotUrl={setSnapshotUrl}
                                        start={startAddress}
                                        end={endAddress}
                                    />
                                </div>

                                {/* Maximum Participants */}
                                <Form.Item
                                    name="max_participants"
                                    label="Maximum Participants"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please enter number of maximum participants",
                                        },
                                        {
                                            type: "number",
                                            min: 3,
                                            message: "Minimum 3 participants required",
                                            transform: (value) => Number(value),
                                        },
                                    ]}
                                >
                                    <InputNumber
                                        min={3}
                                        placeholder="Enter number of participants"
                                        style={{ width: "40%" }}
                                    />
                                </Form.Item>

                                {/* Total Distance */}
                                <Form.Item name="total_distance" label="Total Distance(km.)">
                                    <Input
                                        placeholder="Enter total distance"
                                        style={{ width: "40%" }}
                                        disabled
                                    />
                                </Form.Item>

                                {/* Race Type */}
                                <Form.Item
                                    name="race_type"
                                    label="Race Type"
                                    rules={[{ required: true, message: "Please Select type" }]}
                                >
                                    <Select
                                        size="large"
                                        placeholder="Please select"
                                        style={{ width: "40%" }}
                                        options={raceOptions}
                                    />
                                </Form.Item>

                                {/* Scheduled Time - Only for Marathon */}
                                {formRaceType === "MARATHON" && (
                                    <Form.Item
                                        name="scheduled_time"
                                        label="Start Date & Time"
                                        rules={[
                                            {
                                                required: formRaceType === "MARATHON" ? true : false,
                                                message: "Please select date and time",
                                            },
                                        ]}
                                    >
                                        <DatePicker
                                            showTime
                                            format="YYYY-MM-DD HH:mm"
                                            placeholder="Select date and time"
                                            style={{ width: "40%" }}
                                        />
                                    </Form.Item>
                                )}

                                {/* Completion Time - Only for Marathon */}
                                {formRaceType === "MARATHON" && (
                                    <Form.Item
                                        name="remaining_completion_time"
                                        label="Completion Date & Time"
                                        rules={[
                                            {
                                                required: formRaceType === "MARATHON" ? true : false,
                                                message: "Please select date and time",
                                            },
                                        ]}
                                    >
                                        <DatePicker
                                            showTime
                                            format="YYYY-MM-DD HH:mm"
                                            placeholder="Select date and time"
                                            style={{ width: "40%" }}
                                        />
                                    </Form.Item>
                                )}

                                {/* Race Stop Timer */}
                                <Form.Item
                                    name="race_stop_timer"
                                    label="Race Stop Timer"
                                    rules={[{ required: true, message: "Please Select time" }]}
                                >
                                    <Select
                                        size="large"
                                        placeholder="Please select"
                                        style={{ width: "40%" }}
                                        options={timerOptions}
                                    />
                                </Form.Item>

                                {/* Gender Preference */}
                                <Form.Item
                                    name="gender_preference"
                                    label="Gender Preference"
                                    rules={[{ required: true, message: "Please Select gender" }]}
                                >
                                    <Select
                                        size="large"
                                        placeholder="Please select"
                                        style={{ width: "40%" }}
                                        options={genderOptions}
                                    />
                                </Form.Item>

                                {/* Buttons */}
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <Button
                                        type="default"
                                        onClick={handleGoBack}
                                        style={{ width: "150px" }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button

                                        htmlType="submit"
                                        loading={loading}
                                        style={{
                                            backgroundColor: 'black',
                                            color: 'white',
                                            border: 'none',
                                            width: "150px",
                                            transition: 'background-color 0.3s ease',
                                        }}
                                    >
                                        Update Race
                                    </Button>
                                </div>
                            </Form>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default EditRace;